/**
 * DataPointEditor Component
 * Complete editor for Data Point content items with:
 * - Value input with data type validation
 * - Data type selector (Text, Number, Currency, Date, Percentage, Boolean)
 * - Format string input for number/date formatting
 * - Source type selector (Manual, API, Database, Spreadsheet)
 * - Source configuration form for external data connectors
 * - Preview showing formatted value
 * - Test connection button for external sources
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Separator } from '@/components/ui/separator'
import {
  Type,
  Hash,
  DollarSign,
  Percent,
  Calendar,
  ToggleLeft,
  Database,
  Globe,
  FileSpreadsheet,
  Pencil,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  Eye,
  Info,
  HelpCircle,
} from 'lucide-react'
import type { DataPointContent, DataPointValueType } from '@/lib/api/types'

// ============================================
// Types
// ============================================

export type ContentDataSourceType = 'Manual' | 'Api' | 'Database' | 'Spreadsheet'

export interface DataPointEditorProps {
  value: Partial<DataPointContent>
  onChange: (content: Partial<DataPointContent>) => void
  disabled?: boolean
  className?: string
}

interface SourceConfig {
  sourceType: ContentDataSourceType
  // API config
  apiUrl?: string
  apiMethod?: 'GET' | 'POST'
  apiHeaders?: Record<string, string>
  apiAuthType?: 'none' | 'apiKey' | 'bearer'
  apiAuthValue?: string
  apiJsonPath?: string
  // Database config
  connectionString?: string
  query?: string
  // Spreadsheet config
  fileReference?: string
  sheetName?: string
  cellReference?: string
}

interface DataTypeInfo {
  value: DataPointValueType
  label: string
  description: string
  icon: React.ElementType
  color: string
}

interface FormatExample {
  format: string
  description: string
  example: string
}

// ============================================
// Configuration Constants
// ============================================

const DATA_TYPE_INFO: DataTypeInfo[] = [
  {
    value: 'Text',
    label: 'Text',
    description: 'Plain text value with no formatting',
    icon: Type,
    color: 'text-blue-500',
  },
  {
    value: 'Number',
    label: 'Number',
    description: 'Numeric value with optional decimal places',
    icon: Hash,
    color: 'text-green-500',
  },
  {
    value: 'Currency',
    label: 'Currency',
    description: 'Monetary value with currency symbol',
    icon: DollarSign,
    color: 'text-amber-500',
  },
  {
    value: 'Percentage',
    label: 'Percentage',
    description: 'Percentage value (0-100 or 0-1)',
    icon: Percent,
    color: 'text-purple-500',
  },
  {
    value: 'Date',
    label: 'Date',
    description: 'Date value with format options',
    icon: Calendar,
    color: 'text-rose-500',
  },
  {
    value: 'Boolean',
    label: 'Boolean',
    description: 'True/False value with custom labels',
    icon: ToggleLeft,
    color: 'text-cyan-500',
  },
]

const SOURCE_TYPE_INFO: { value: ContentDataSourceType; label: string; icon: React.ElementType; description: string }[] = [
  {
    value: 'Manual',
    label: 'Manual Entry',
    icon: Pencil,
    description: 'Enter value manually',
  },
  {
    value: 'Api',
    label: 'API',
    icon: Globe,
    description: 'Fetch from REST API',
  },
  {
    value: 'Database',
    label: 'Database',
    icon: Database,
    description: 'Query from database',
  },
  {
    value: 'Spreadsheet',
    label: 'Spreadsheet',
    icon: FileSpreadsheet,
    description: 'Read from Excel/Sheets',
  },
]

const CURRENCY_OPTIONS = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '\u20AC', name: 'Euro' },
  { code: 'GBP', symbol: '\u00A3', name: 'British Pound' },
  { code: 'JPY', symbol: '\u00A5', name: 'Japanese Yen' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CNY', symbol: '\u00A5', name: 'Chinese Yuan' },
  { code: 'INR', symbol: '\u20B9', name: 'Indian Rupee' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
]

const NUMBER_FORMAT_EXAMPLES: FormatExample[] = [
  { format: '#,##0', description: 'Thousands separator', example: '1,234' },
  { format: '#,##0.00', description: 'Two decimals', example: '1,234.56' },
  { format: '0.00', description: 'Fixed decimals', example: '1234.56' },
  { format: '#,##0.###', description: 'Up to 3 decimals', example: '1,234.567' },
  { format: '0%', description: 'Percentage (multiplied)', example: '12%' },
  { format: '0.00%', description: 'Percent with decimals', example: '12.34%' },
]

const DATE_FORMAT_EXAMPLES: FormatExample[] = [
  { format: 'MM/dd/yyyy', description: 'US format', example: '01/31/2026' },
  { format: 'dd/MM/yyyy', description: 'EU format', example: '31/01/2026' },
  { format: 'yyyy-MM-dd', description: 'ISO format', example: '2026-01-31' },
  { format: 'MMMM d, yyyy', description: 'Long format', example: 'January 31, 2026' },
  { format: 'MMM d, yyyy', description: 'Short month', example: 'Jan 31, 2026' },
  { format: 'EEE, MMM d', description: 'With day name', example: 'Sat, Jan 31' },
]

// ============================================
// Helper Functions
// ============================================

/**
 * Format a value based on its type and format string
 */
function formatValue(
  value: string | undefined,
  valueType: DataPointValueType,
  format?: string,
  currencyCode?: string,
  decimalPlaces?: number
): string {
  if (!value || value === '') return '—'

  try {
    switch (valueType) {
      case 'Text':
        return value

      case 'Number': {
        const num = parseFloat(value)
        if (isNaN(num)) return value
        const decimals = decimalPlaces ?? 0
        return num.toLocaleString('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })
      }

      case 'Currency': {
        const num = parseFloat(value)
        if (isNaN(num)) return value
        const currency = CURRENCY_OPTIONS.find(c => c.code === currencyCode)
        const decimals = decimalPlaces ?? 2
        const formatted = num.toLocaleString('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })
        return currency ? `${currency.symbol}${formatted}` : `$${formatted}`
      }

      case 'Percentage': {
        const num = parseFloat(value)
        if (isNaN(num)) return value
        const decimals = decimalPlaces ?? 0
        // Assume value is already in percentage form (0-100)
        return `${num.toFixed(decimals)}%`
      }

      case 'Date': {
        const date = new Date(value)
        if (isNaN(date.getTime())) return value
        // Use format string or default
        if (format === 'yyyy-MM-dd') {
          return date.toISOString().split('T')[0] ?? value
        }
        if (format === 'MMMM d, yyyy') {
          return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        }
        if (format === 'MMM d, yyyy') {
          return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        }
        if (format === 'dd/MM/yyyy') {
          const d = date.getDate().toString().padStart(2, '0')
          const m = (date.getMonth() + 1).toString().padStart(2, '0')
          const y = date.getFullYear()
          return `${d}/${m}/${y}`
        }
        // Default to MM/dd/yyyy
        return date.toLocaleDateString('en-US')
      }

      case 'Boolean': {
        const lower = value.toLowerCase()
        if (lower === 'true' || lower === '1' || lower === 'yes') return 'Yes'
        if (lower === 'false' || lower === '0' || lower === 'no') return 'No'
        return value
      }

      default:
        return value
    }
  } catch {
    return value
  }
}

/**
 * Validate value based on data type
 */
function validateValue(value: string, valueType: DataPointValueType): { valid: boolean; error?: string } {
  if (!value) return { valid: true }

  switch (valueType) {
    case 'Number':
    case 'Currency':
    case 'Percentage': {
      const num = parseFloat(value)
      if (isNaN(num)) {
        return { valid: false, error: 'Please enter a valid number' }
      }
      if (valueType === 'Percentage' && (num < 0 || num > 100)) {
        return { valid: false, error: 'Percentage should be between 0 and 100' }
      }
      return { valid: true }
    }

    case 'Date': {
      const date = new Date(value)
      if (isNaN(date.getTime())) {
        return { valid: false, error: 'Please enter a valid date' }
      }
      return { valid: true }
    }

    case 'Boolean': {
      const lower = value.toLowerCase()
      const validValues = ['true', 'false', '1', '0', 'yes', 'no']
      if (!validValues.includes(lower)) {
        return { valid: false, error: 'Please enter true/false, yes/no, or 1/0' }
      }
      return { valid: true }
    }

    default:
      return { valid: true }
  }
}

// ============================================
// DataTypeSelector Component
// ============================================

interface DataTypeSelectorProps {
  value: DataPointValueType
  onChange: (type: DataPointValueType) => void
  disabled?: boolean
}

function DataTypeSelector({ value, onChange, disabled }: DataTypeSelectorProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Data Type</Label>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="h-4 w-4 text-neutral-400 cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-xs">
              <p>Choose the type of data this point will hold. This affects validation and formatting options.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {DATA_TYPE_INFO.map((type) => {
          const Icon = type.icon
          const isSelected = value === type.value
          return (
            <TooltipProvider key={type.value} delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => onChange(type.value)}
                    disabled={disabled}
                    className={cn(
                      'flex items-center gap-2 p-3 rounded-xl border text-left transition-all',
                      'hover:border-neutral-300 dark:hover:border-neutral-600',
                      isSelected
                        ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900',
                      disabled && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    <Icon className={cn('h-4 w-4', type.color)} />
                    <span className={cn(
                      'text-sm font-medium',
                      isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-neutral-700 dark:text-neutral-300'
                    )}>
                      {type.label}
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {type.description}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        })}
      </div>
    </div>
  )
}

// ============================================
// Type-Specific Value Inputs
// ============================================

interface ValueInputProps {
  valueType: DataPointValueType
  value: string
  onChange: (value: string) => void
  currencyCode?: string
  onCurrencyChange?: (code: string) => void
  disabled?: boolean
  error?: string
}

function ValueInput({
  valueType,
  value,
  onChange,
  currencyCode = 'USD',
  onCurrencyChange,
  disabled,
  error,
}: ValueInputProps) {
  const [booleanLabels] = useState({ true: 'Yes', false: 'No' })

  switch (valueType) {
    case 'Text':
      return (
        <div className="space-y-2">
          <Label htmlFor="text-value">Value</Label>
          <Input
            id="text-value"
            type="text"
            placeholder="Enter text value"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            error={!!error}
            className="rounded-xl"
          />
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      )

    case 'Number':
      return (
        <div className="space-y-2">
          <Label htmlFor="number-value">Value</Label>
          <Input
            id="number-value"
            type="number"
            placeholder="Enter numeric value"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            error={!!error}
            className="rounded-xl"
            step="any"
          />
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      )

    case 'Currency':
      return (
        <div className="space-y-2">
          <Label>Value</Label>
          <div className="flex gap-2">
            <Select
              value={currencyCode}
              onValueChange={(code) => onCurrencyChange?.(code)}
              disabled={disabled}
            >
              <SelectTrigger className="w-28 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCY_OPTIONS.map((currency) => (
                  <SelectItem key={currency.code} value={currency.code}>
                    {currency.code} ({currency.symbol})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number"
              placeholder="Enter amount"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              error={!!error}
              className="rounded-xl flex-1"
              step="0.01"
            />
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      )

    case 'Percentage':
      return (
        <div className="space-y-2">
          <Label htmlFor="percentage-value">Value (0-100)</Label>
          <div className="relative">
            <Input
              id="percentage-value"
              type="number"
              placeholder="Enter percentage"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              error={!!error}
              className="rounded-xl pr-8"
              min={0}
              max={100}
              step="0.01"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
              %
            </span>
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      )

    case 'Date':
      return (
        <div className="space-y-2">
          <Label htmlFor="date-value">Value</Label>
          <Input
            id="date-value"
            type="date"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            error={!!error}
            className="rounded-xl"
          />
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      )

    case 'Boolean':
      return (
        <div className="space-y-2">
          <Label>Value</Label>
          <div className="flex items-center gap-4 p-3 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50">
            <div className="flex items-center gap-3">
              <Switch
                checked={value === 'true' || value === '1' || value === 'yes'}
                onCheckedChange={(checked) => onChange(checked ? 'true' : 'false')}
                disabled={disabled}
              />
              <span className={cn(
                'text-sm font-medium',
                (value === 'true' || value === '1' || value === 'yes')
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-neutral-500'
              )}>
                {(value === 'true' || value === '1' || value === 'yes')
                  ? booleanLabels.true
                  : booleanLabels.false
                }
              </span>
            </div>
          </div>
        </div>
      )

    default:
      return (
        <div className="space-y-2">
          <Label htmlFor="value">Value</Label>
          <Input
            id="value"
            type="text"
            placeholder="Enter value"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className="rounded-xl"
          />
        </div>
      )
  }
}

// ============================================
// FormatInput Component
// ============================================

interface FormatInputProps {
  valueType: DataPointValueType
  format?: string
  onFormatChange: (format: string) => void
  decimalPlaces?: number
  onDecimalPlacesChange: (places: number) => void
  unit?: string
  onUnitChange: (unit: string) => void
  disabled?: boolean
}

function FormatInput({
  valueType,
  format,
  onFormatChange,
  decimalPlaces = 0,
  onDecimalPlacesChange,
  unit,
  onUnitChange,
  disabled,
}: FormatInputProps) {
  const formatExamples = useMemo(() => {
    switch (valueType) {
      case 'Number':
      case 'Currency':
      case 'Percentage':
        return NUMBER_FORMAT_EXAMPLES
      case 'Date':
        return DATE_FORMAT_EXAMPLES
      default:
        return []
    }
  }, [valueType])

  if (valueType === 'Text' || valueType === 'Boolean') {
    return null
  }

  return (
    <div className="space-y-4">
      <Separator />
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          Formatting Options
        </h4>

        {/* Decimal Places for numeric types */}
        {(valueType === 'Number' || valueType === 'Currency' || valueType === 'Percentage') && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="decimal-places">Decimal Places</Label>
              <Select
                value={decimalPlaces.toString()}
                onValueChange={(val) => onDecimalPlacesChange(parseInt(val))}
                disabled={disabled}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0 (no decimals)</SelectItem>
                  <SelectItem value="1">1 decimal</SelectItem>
                  <SelectItem value="2">2 decimals</SelectItem>
                  <SelectItem value="3">3 decimals</SelectItem>
                  <SelectItem value="4">4 decimals</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {valueType === 'Number' && (
              <div className="space-y-2">
                <Label htmlFor="unit">Unit (optional)</Label>
                <Input
                  id="unit"
                  type="text"
                  placeholder="e.g., users, items, kg"
                  value={unit || ''}
                  onChange={(e) => onUnitChange(e.target.value)}
                  disabled={disabled}
                  className="rounded-xl"
                />
              </div>
            )}
          </div>
        )}

        {/* Date Format */}
        {valueType === 'Date' && (
          <div className="space-y-2">
            <Label>Date Format</Label>
            <Select
              value={format || 'MM/dd/yyyy'}
              onValueChange={onFormatChange}
              disabled={disabled}
            >
              <SelectTrigger className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_FORMAT_EXAMPLES.map((example) => (
                  <SelectItem key={example.format} value={example.format}>
                    {example.description} ({example.example})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Format Reference */}
        {formatExamples.length > 0 && valueType !== 'Date' && (
          <div className="p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700">
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
              <Info className="h-3.5 w-3.5 inline mr-1" />
              Common format patterns:
            </p>
            <div className="flex flex-wrap gap-2">
              {formatExamples.slice(0, 4).map((example) => (
                <code
                  key={example.format}
                  className="text-xs px-2 py-1 bg-white dark:bg-neutral-900 rounded border border-neutral-200 dark:border-neutral-700"
                >
                  {example.format} = {example.example}
                </code>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================
// SourceConfigForm Component
// ============================================

interface SourceConfigFormProps {
  sourceType: ContentDataSourceType
  config: SourceConfig
  onChange: (config: SourceConfig) => void
  onTestConnection: () => Promise<{ success: boolean; message: string }>
  disabled?: boolean
}

function SourceConfigForm({
  sourceType,
  config,
  onChange,
  onTestConnection,
  disabled,
}: SourceConfigFormProps) {
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  const handleTestConnection = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const result = await onTestConnection()
      setTestResult(result)
    } catch (err) {
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Connection test failed',
      })
    } finally {
      setTesting(false)
    }
  }

  if (sourceType === 'Manual') {
    return null
  }

  return (
    <div className="space-y-4">
      <Separator />
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            Data Source Configuration
          </h4>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleTestConnection}
            disabled={disabled || testing}
            className="rounded-lg"
          >
            {testing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-1.5">Test Connection</span>
          </Button>
        </div>

        {/* Test Result Alert */}
        {testResult && (
          <div
            className={cn(
              'p-3 rounded-lg flex items-start gap-2',
              testResult.success
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
            )}
          >
            {testResult.success ? (
              <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
            ) : (
              <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            )}
            <span className="text-sm">{testResult.message}</span>
          </div>
        )}

        {/* API Configuration */}
        {sourceType === 'Api' && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2 space-y-2">
                <Label htmlFor="api-url">API URL</Label>
                <Input
                  id="api-url"
                  type="url"
                  placeholder="https://api.example.com/data"
                  value={config.apiUrl || ''}
                  onChange={(e) => onChange({ ...config, apiUrl: e.target.value })}
                  disabled={disabled}
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label>HTTP Method</Label>
                <Select
                  value={config.apiMethod || 'GET'}
                  onValueChange={(val) => onChange({ ...config, apiMethod: val as 'GET' | 'POST' })}
                  disabled={disabled}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Authentication</Label>
                <Select
                  value={config.apiAuthType || 'none'}
                  onValueChange={(val) => onChange({ ...config, apiAuthType: val as 'none' | 'apiKey' | 'bearer' })}
                  disabled={disabled}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Auth</SelectItem>
                    <SelectItem value="apiKey">API Key</SelectItem>
                    <SelectItem value="bearer">Bearer Token</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {config.apiAuthType && config.apiAuthType !== 'none' && (
              <div className="space-y-2">
                <Label htmlFor="auth-value">
                  {config.apiAuthType === 'apiKey' ? 'API Key' : 'Bearer Token'}
                </Label>
                <Input
                  id="auth-value"
                  type="password"
                  placeholder={config.apiAuthType === 'apiKey' ? 'Enter API key' : 'Enter bearer token'}
                  value={config.apiAuthValue || ''}
                  onChange={(e) => onChange({ ...config, apiAuthValue: e.target.value })}
                  disabled={disabled}
                  className="rounded-xl"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="json-path">JSON Path to Value</Label>
              <Input
                id="json-path"
                type="text"
                placeholder="e.g., data.result.value or $.data[0].price"
                value={config.apiJsonPath || ''}
                onChange={(e) => onChange({ ...config, apiJsonPath: e.target.value })}
                disabled={disabled}
                className="rounded-xl"
              />
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                The path to extract the value from the JSON response
              </p>
            </div>
          </div>
        )}

        {/* Database Configuration */}
        {sourceType === 'Database' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="connection-string">Connection String</Label>
              <Input
                id="connection-string"
                type="password"
                placeholder="Enter database connection string"
                value={config.connectionString || ''}
                onChange={(e) => onChange({ ...config, connectionString: e.target.value })}
                disabled={disabled}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="query">SQL Query</Label>
              <Input
                id="query"
                type="text"
                placeholder="SELECT value FROM table WHERE id = 1"
                value={config.query || ''}
                onChange={(e) => onChange({ ...config, query: e.target.value })}
                disabled={disabled}
                className="rounded-xl font-mono text-sm"
              />
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Query must return a single value
              </p>
            </div>
          </div>
        )}

        {/* Spreadsheet Configuration */}
        {sourceType === 'Spreadsheet' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="file-reference">File Reference</Label>
              <Input
                id="file-reference"
                type="text"
                placeholder="File path or SharePoint URL"
                value={config.fileReference || ''}
                onChange={(e) => onChange({ ...config, fileReference: e.target.value })}
                disabled={disabled}
                className="rounded-xl"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="sheet-name">Sheet Name</Label>
                <Input
                  id="sheet-name"
                  type="text"
                  placeholder="e.g., Sheet1"
                  value={config.sheetName || ''}
                  onChange={(e) => onChange({ ...config, sheetName: e.target.value })}
                  disabled={disabled}
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cell-reference">Cell Reference</Label>
                <Input
                  id="cell-reference"
                  type="text"
                  placeholder="e.g., A1, B5"
                  value={config.cellReference || ''}
                  onChange={(e) => onChange({ ...config, cellReference: e.target.value })}
                  disabled={disabled}
                  className="rounded-xl"
                />
              </div>
            </div>
          </div>
        )}

        {/* Refresh Interval */}
        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              External data source connections are in preview. Data will be cached and refreshed
              based on your tenant's settings.
            </span>
          </p>
        </div>
      </div>
    </div>
  )
}

// ============================================
// FormattedValuePreview Component
// ============================================

interface FormattedValuePreviewProps {
  value: string
  valueType: DataPointValueType
  format?: string
  currencyCode?: string
  decimalPlaces?: number
  unit?: string
  className?: string
}

function FormattedValuePreview({
  value,
  valueType,
  format,
  currencyCode,
  decimalPlaces,
  unit,
  className,
}: FormattedValuePreviewProps) {
  const formattedValue = useMemo(() => {
    const formatted = formatValue(value, valueType, format, currencyCode, decimalPlaces)
    if (unit && formatted !== '—') {
      return `${formatted} ${unit}`
    }
    return formatted
  }, [value, valueType, format, currencyCode, decimalPlaces, unit])

  const typeInfo = DATA_TYPE_INFO.find(t => t.value === valueType)
  const Icon = typeInfo?.icon || Hash

  return (
    <div className={cn(
      'p-4 rounded-xl border-2 border-dashed border-neutral-200 dark:border-neutral-700',
      'bg-gradient-to-br from-neutral-50 to-white dark:from-neutral-900 dark:to-neutral-800',
      className
    )}>
      <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 mb-2">
        <Eye className="h-3.5 w-3.5" />
        <span>Preview</span>
      </div>
      <div className="flex items-center gap-3">
        <div className={cn(
          'p-2 rounded-lg bg-white dark:bg-neutral-800 shadow-sm',
          'border border-neutral-100 dark:border-neutral-700'
        )}>
          <Icon className={cn('h-5 w-5', typeInfo?.color || 'text-neutral-500')} />
        </div>
        <span className={cn(
          'text-2xl font-semibold tabular-nums',
          formattedValue === '—'
            ? 'text-neutral-300 dark:text-neutral-600'
            : 'text-neutral-900 dark:text-white'
        )}>
          {formattedValue}
        </span>
      </div>
    </div>
  )
}

// ============================================
// Main DataPointEditor Component
// ============================================

export function DataPointEditor({
  value,
  onChange,
  disabled = false,
  className,
}: DataPointEditorProps) {
  // Local state for source configuration (not in DataPointContent)
  const [sourceType, setSourceType] = useState<ContentDataSourceType>(
    value.isLive ? 'Api' : 'Manual'
  )
  const [sourceConfig, setSourceConfig] = useState<SourceConfig>({
    sourceType: 'Manual',
  })
  const [valueError, setValueError] = useState<string | undefined>()

  // Update source type when isLive changes
  useEffect(() => {
    if (value.isLive && value.dataSourceId) {
      setSourceType('Api')
    }
  }, [value.isLive, value.dataSourceId])

  // Handle value change with validation
  const handleValueChange = useCallback(
    (newValue: string) => {
      const validation = validateValue(newValue, value.valueType || 'Text')
      setValueError(validation.error)

      onChange({
        ...value,
        currentValue: newValue,
        previousValue: value.currentValue,
      })
    },
    [value, onChange]
  )

  // Handle data type change
  const handleTypeChange = useCallback(
    (newType: DataPointValueType) => {
      // Reset value when type changes to avoid validation issues
      onChange({
        ...value,
        valueType: newType,
        currentValue: '',
        format: undefined,
        decimalPlaces: newType === 'Currency' ? 2 : 0,
      })
      setValueError(undefined)
    },
    [value, onChange]
  )

  // Handle source type change
  const handleSourceTypeChange = useCallback(
    (newSourceType: ContentDataSourceType) => {
      setSourceType(newSourceType)
      setSourceConfig({ sourceType: newSourceType })
      onChange({
        ...value,
        isLive: newSourceType !== 'Manual',
      })
    },
    [value, onChange]
  )

  // Test connection handler (simulated for now)
  const handleTestConnection = useCallback(async (): Promise<{ success: boolean; message: string }> => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500))

    // For demo purposes, validate that required fields are filled
    if (sourceType === 'Api' && !sourceConfig.apiUrl) {
      return { success: false, message: 'Please enter an API URL' }
    }
    if (sourceType === 'Database' && !sourceConfig.connectionString) {
      return { success: false, message: 'Please enter a connection string' }
    }
    if (sourceType === 'Spreadsheet' && !sourceConfig.fileReference) {
      return { success: false, message: 'Please enter a file reference' }
    }

    // Simulated success
    return { success: true, message: 'Connection successful! Data source is accessible.' }
  }, [sourceType, sourceConfig])

  return (
    <div className={cn('space-y-6', className)}>
      {/* Data Type Selector */}
      <DataTypeSelector
        value={value.valueType || 'Text'}
        onChange={handleTypeChange}
        disabled={disabled}
      />

      {/* Source Type Selector */}
      <div className="space-y-2">
        <Label>Data Source</Label>
        <div className="flex flex-wrap gap-2">
          {SOURCE_TYPE_INFO.map((source) => {
            const Icon = source.icon
            const isSelected = sourceType === source.value
            return (
              <TooltipProvider key={source.value} delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => handleSourceTypeChange(source.value)}
                      disabled={disabled}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all',
                        'hover:border-neutral-300 dark:hover:border-neutral-600',
                        isSelected
                          ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                          : 'border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400',
                        disabled && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="font-medium">{source.label}</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    {source.description}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          })}
        </div>
      </div>

      {/* Value Input (for Manual source) */}
      {sourceType === 'Manual' && (
        <ValueInput
          valueType={value.valueType || 'Text'}
          value={value.currentValue || ''}
          onChange={handleValueChange}
          currencyCode={value.currencyCode}
          onCurrencyChange={(code) => onChange({ ...value, currencyCode: code })}
          disabled={disabled}
          error={valueError}
        />
      )}

      {/* Source Configuration (for external sources) */}
      <SourceConfigForm
        sourceType={sourceType}
        config={sourceConfig}
        onChange={setSourceConfig}
        onTestConnection={handleTestConnection}
        disabled={disabled}
      />

      {/* Format Input */}
      <FormatInput
        valueType={value.valueType || 'Text'}
        format={value.format}
        onFormatChange={(format) => onChange({ ...value, format })}
        decimalPlaces={value.decimalPlaces ?? (value.valueType === 'Currency' ? 2 : 0)}
        onDecimalPlacesChange={(places) => onChange({ ...value, decimalPlaces: places })}
        unit={value.unit}
        onUnitChange={(unit) => onChange({ ...value, unit })}
        disabled={disabled}
      />

      {/* Formatted Value Preview */}
      <FormattedValuePreview
        value={value.currentValue || ''}
        valueType={value.valueType || 'Text'}
        format={value.format}
        currencyCode={value.currencyCode}
        decimalPlaces={value.decimalPlaces}
        unit={value.unit}
      />
    </div>
  )
}

export default DataPointEditor
